import React, { useState, useMemo, useEffect, useContext } from "react";
import { Modal, message } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";

import { TaskTemplate } from "../../../types/taskTemplate";
import TaskTemplateFilters from "./components/taskTemplates/TaskTemplateFilters";
import TaskTemplateGrid from "./components/taskTemplates/TaskTemplateGrid";
import TaskTemplateModal from "./components/taskTemplates/TaskTemplateModal";
import { TaskTemplateOperation } from "../../../serviceoperation/TaskTemplateOperation";
import { CompanyContext } from "../../../contexts/company-context";
import { ResponseTaskTemplateResponse } from "../../../serviceoperation/entity/TaskTemplateOperationEntity";

const { confirm } = Modal;

const TaskTemplatesTab: React.FC = () => {
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const { selectedCompany } = useContext(CompanyContext);
  const tempOps = new TaskTemplateOperation();

  useEffect(() => {
    const loadTaskTemplates = async () => {
      if (!selectedCompany?.corporationId) {
        setTemplates([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const value = await tempOps.getTaskTemplateAsync(
          selectedCompany.corporationId,
          1,
          255,
        );
        const payload = value as ResponseTaskTemplateResponse & {
          data?: ResponseTaskTemplateResponse;
        };
        const dataResults = payload.data?.results;
        const results = Array.isArray(payload?.results)
          ? payload.results
          : Array.isArray(dataResults)
          ? dataResults
          : [];

        setTemplates(
          results
            .map((x) => {
              const row = x as unknown as {
                taskTemplateId?: string | number;
                templateId?: string | number;
                id?: string | number;
                name?: string;
                description?: string;
                category?: string;
                branchId?: number;
                items?: unknown[];
                isDefault?: boolean;
                isActive?: boolean;
                activeInd?: boolean;
                createdDate?: string;
                createdDateTimeUTC?: string;
              };

              const resolvedId = row.taskTemplateId ?? row.templateId ?? row.id;

              return {
                id:
                  resolvedId !== undefined && resolvedId !== null
                    ? String(resolvedId)
                    : "",
                name: row.name ?? "",
                description: row.description,
                category: row.category,
                branchId: row.branchId,
                items: Array.isArray(row.items)
                  ? (row.items as TaskTemplate["items"])
                  : [],
                isDefault: !!row.isDefault,
                isActive: row.isActive ?? row.activeInd ?? true,
                createdAt:
                  row.createdDate ??
                  row.createdDateTimeUTC ??
                  new Date().toISOString(),
                updatedAt:
                  row.createdDate ??
                  row.createdDateTimeUTC ??
                  new Date().toISOString(),
              };
            })
            .filter((template) => !!template.id),
        );
      } catch (error) {
        setTemplates([]);
        message.error("Unable to load task templates. Please try again.");
        console.error("Task template load failed:", error);
      } finally {
        setLoading(false);
      }
    };

    loadTaskTemplates();
  }, [selectedCompany]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TaskTemplate | null>(
    null,
  );
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const filteredTemplates = useMemo(
    () =>
      templates?.filter(
        (template) =>
          template.isActive &&
          (template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (template.description?.toLowerCase() || "").includes(
              searchQuery.toLowerCase(),
            )),
      ),
    [templates, searchQuery],
  );

  const handleAddTemplate = () => {
    setModalMode("create");
    setEditingTemplate(null);
    setIsModalOpen(true);
  };

  const handleEditTemplate = (template: TaskTemplate) => {
    if (!template.isActive) {
      message.error("Deleted task templates cannot be modified.");
      return;
    }

    setModalMode("edit");
    setEditingTemplate(template);
    setIsModalOpen(true);
  };

  const handleSaveTemplate = (templateData: Partial<TaskTemplate>) => {
    if (modalMode === "create") {
      const newTemplate: TaskTemplate = {
        id: templateData.id || `template_${Date.now()}`,
        name: templateData.name ?? "",
        description: templateData.description,
        category: templateData.category,
        branchId: templateData.branchId,
        items: templateData.items || [],
        isDefault: templateData.isDefault || false,
        isActive: templateData.isActive ?? true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // If setting as default, remove default from other templates
      if (newTemplate.isDefault) {
        setTemplates((prev) => prev.map((t) => ({ ...t, isDefault: false })));
      }

      setTemplates((prev) => [...prev, newTemplate]);
    } else if (editingTemplate) {
      setTemplates((prev) =>
        prev.map((template) =>
          template.id === editingTemplate.id
            ? {
                ...template,
                ...templateData,
                updatedAt: new Date().toISOString(),
              }
            : template,
        ),
      );
    }

    setIsModalOpen(false);
    setEditingTemplate(null);
  };

  const handleDeleteTemplate = (id: string) => {
    const template = templates.find((item) => item.id === id);
    if (template && !template.isActive) {
      message.error("Deleted task templates cannot be modified.");
      return;
    }

    confirm({
      title: "Delete Task Template",
      icon: <ExclamationCircleOutlined />,
      content: "Are you sure you want to delete this task template?",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      async onOk() {
        if (!selectedCompany?.corporationId) {
          message.error("An error has occurred while processing your request!");
          return;
        }

        try {
          const value = await tempOps.deleteTaskTemplateAsync(
            selectedCompany.corporationId,
            id,
          );

          if (value.statusCode === 200) {
            setTemplates((prev) =>
              prev.filter((template) => template.id !== id),
            );
            message.success("Task template deleted successfully");
          } else {
            message.error(
              value.statusMessage ||
                "An error has occurred while processing your request!",
            );
          }
        } catch (error) {
          message.error("An error has occurred while processing your request!");
          console.error("Task template delete failed:", error);
        }
      },
    });
  };

  const handleSetDefault = (id: string) => {
    setTemplates((prev) =>
      prev.map((template) => ({
        ...template,
        isDefault: template.id === id,
      })),
    );
    message.success("Default template updated");
  };

  return (
    <div style={{ padding: "24px" }}>
      <TaskTemplateFilters
        onSearch={setSearchQuery}
        onAddTemplate={handleAddTemplate}
        totalTemplates={filteredTemplates?.length || 0}
      />

      <div style={{ marginTop: 24 }}>
        <TaskTemplateGrid
          templates={filteredTemplates || []}
          loading={loading}
          onEdit={handleEditTemplate}
          onDelete={handleDeleteTemplate}
          onSetDefault={handleSetDefault}
        />
      </div>

      <TaskTemplateModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingTemplate(null);
        }}
        onSave={handleSaveTemplate}
        template={editingTemplate}
        mode={modalMode}
      />
    </div>
  );
};

export default TaskTemplatesTab;
